import type { AppRole, OperationSummary } from "../types";

/**
 * Almikia Epic E4 (TASK-027): pure UI rules for the operations screens. The
 * database stays the authority (RLS and SECURITY DEFINER functions); these
 * helpers only decide which controls to render so a role never sees a
 * control that would be rejected.
 */

/** List columns, in display order. */
export const OPERATION_LIST_COLUMNS = [
  "id",
  "project_name",
  "unit_identifier",
  "sales_name",
  "commercial_status",
  "administrative_status",
  "semaphore",
  "sale_value",
  "contract_folio",
] as const;

/** Roles that see the rows of several advisors (so the advisor filter applies). */
const MULTI_ADVISOR_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "lider",
  "auditoria",
  "administracion",
  "compliance",
];

/** Filter sources shown on the list for `role` (search is always on). */
export const getOperationFilterSources = (role: AppRole): string[] => {
  const sources = [
    "project_id",
    "commercial_status",
    "administrative_status",
    "semaphore",
  ];
  if (MULTI_ADVISOR_ROLES.includes(role)) sources.push("sales_id");
  return sources;
};

/** Roles that may sign / cancel besides the owning advisor. */
const ACT_ROLES: AppRole[] = ["direccion", "lead_manager", "administracion"];
const ADMIN_EDIT_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "administracion",
];
const OWNER_ACT_ROLES: AppRole[] = ["asesora", "lider"];

export type OperationControls = {
  canSign: boolean;
  canCancel: boolean;
  canChangeAdvisor: boolean;
  /** Edits estado administrativo, tipo, client link. */
  canEditAdministrative: boolean;
  /** Edits semaforo and its reason (also compliance). */
  canEditSemaphore: boolean;
};

/**
 * Which controls `role` sees on an operation. A cancelled or signed
 * operation offers no sign; a signed or cancelled one offers no cancel.
 * `isOwner` is true when the viewer is the operation's advisor.
 */
export const getOperationControls = (
  role: AppRole,
  operation: Pick<OperationSummary, "commercial_status" | "option_id">,
  isOwner: boolean,
): OperationControls => {
  // Corretaje operations (option_id set) close / cancel under the same gates as BPO (QEC-14).
  const isOpen = operation.commercial_status === "apartada";
  const canAct =
    ACT_ROLES.includes(role) || (OWNER_ACT_ROLES.includes(role) && isOwner);
  return {
    canSign: isOpen && canAct,
    canCancel: isOpen && canAct,
    canChangeAdvisor:
      operation.commercial_status !== "cancelada" &&
      (role === "direccion" || role === "lead_manager"),
    canEditAdministrative: ADMIN_EDIT_ROLES.includes(role),
    canEditSemaphore: ADMIN_EDIT_ROLES.includes(role) || role === "compliance",
  };
};

/**
 * Almikia Epic EC: who edits a corretaje sale administration and requests the
 * VoBo (QEC-12; a lead_manager only within her scope, which the server checks).
 * The vendedora and her lider team only read it.
 */
const SALE_EDIT_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "administracion",
];
export const canEditCorretajeSale = (role: AppRole): boolean =>
  SALE_EDIT_ROLES.includes(role);

/** Only direccion grants, flags or reopens the VoBo (QEC-11). */
export const canDecideCorretajeVobo = (role: AppRole): boolean =>
  role === "direccion";

/** Roles whose RLS lets them read the client behind an operation. */
const CLIENT_READ_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
  "auditoria",
];

/** The client link is shown only to roles that can read clients. */
export const canSeeClient = (role: AppRole): boolean =>
  CLIENT_READ_ROLES.includes(role);

/**
 * contabilidad reads the operation and the client name only (TASK-058, QE4-8): the
 * administrative form, documents and advisor history stay hidden from her, like in the RLS.
 */
export const canSeeOperationDetail = (role: AppRole): boolean =>
  role !== "contabilidad";

/**
 * Folio preview `{PROJ}-{UNIT}-{YYMMDD}` for a signature date (`YYYY-MM-DD`);
 * null while the date is not a valid ISO date. Mirrors `sign_operation()`.
 */
export const previewFolio = (
  projectCode: string | null | undefined,
  unitIdentifier: string | null | undefined,
  isoDate: string,
): string | null => {
  if (!projectCode || !unitIdentifier) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  return `${projectCode}-${unitIdentifier}-${isoDate.slice(2, 4)}${isoDate.slice(5, 7)}${isoDate.slice(8, 10)}`;
};

/** Today as `YYYY-MM-DD` in local time. */
export const todayIso = (now: Date = new Date()): string => {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};
